import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Brain, 
  MessageSquare, 
  Send, 
  X, 
  ThumbsUp, 
  ThumbsDown, 
  Clock, 
  ArrowLeft, 
  Sparkles, 
  Check, 
  AlertCircle
} from 'lucide-react';
import API from '../api_config';
import { useAuth } from '../contexts/AuthContext';

export default function HelpAgentWidget() {
  const { user } = useAuth();
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatId, setChatId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'history' | 'learnings'
  const [pastChats, setPastChats] = useState([]);
  const [learnings, setLearnings] = useState([]);
  const [feedbackSent, setFeedbackSent] = useState({}); // messageId: { rating: 1 | -1 }
  
  const messagesEndRef = useRef(null);

  const getModuleWelcomeText = () => {
    if (!user) return 'el sistema';
    if (user.role === 'superadmin') {
      return 'las herramientas de administración global de **SaaS, Registro de Empresas y Actividad Global**';
    }
    
    const names = [];
    const mods = user.active_modules || [];
    if (mods.includes('pos')) names.push('**Punto de Venta (POS)**');
    if (mods.includes('inventory')) names.push('**Inventario**');
    if (mods.includes('purchases')) names.push('**Adquisiciones**');
    if (mods.includes('construction')) names.push('**Construcción Chilena**');
    if (mods.includes('reports') || mods.includes('sales')) names.push('**Ventas y Clientes**');
    
    if (names.length === 0) {
      return 'los módulos activos de tu cuenta';
    } else if (names.length === 1) {
      return `el módulo de ${names[0]}`;
    } else {
      const last = names.pop();
      return `los módulos de ${names.join(', ')} y ${last}`;
    }
  };

  useEffect(() => {
    if (!user) return;
    if (isOpen) {
      scrollToBottom();
      if (messages.length === 0 && activeTab === 'chat') {
        const welcomeModules = getModuleWelcomeText();
        // Mensaje inicial de bienvenida
        setMessages([
          {
            id: 'welcome',
            role: 'assistant',
            content: `¡Hola **${user.full_name}**! 👋 Soy tu asistente GKServicePOS.\n\nEstoy aquí para ayudarte a usar ${welcomeModules}.\n\n*Recuerda: Aprendo detalles específicos de tu negocio (como horarios o reglas internas) mientras conversamos para responder mejor la próxima vez.* ✨`,
            timestamp: new Date().toISOString()
          }
        ]);
      }
    }
  }, [isOpen, messages.length, activeTab, user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // No renderizar si no hay usuario (se ejecuta después de todos los hooks)
  if (!user) {
    return null;
  }

  // Cargar chats históricos
  const loadPastChats = async () => {
    try {
      const response = await axios.get(`${API}/agent/chats`);
      setPastChats(response.data);
    } catch (error) {
      console.error('Error al cargar chats históricos:', error);
    }
  };

  // Cargar aprendizajes de la empresa
  const loadLearnings = async () => {
    if (user.role !== 'admin') return;
    try {
      const response = await axios.get(`${API}/agent/learnings`);
      setLearnings(response.data);
    } catch (error) {
      console.error('Error al cargar aprendizajes:', error);
    }
  };

  // Manejar cambio de pestañas
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'history') {
      loadPastChats();
    } else if (tab === 'learnings') {
      loadLearnings();
    }
  };

  // Cargar un chat histórico específico
  const handleSelectChat = async (id) => {
    setLoading(true);
    setActiveTab('chat');
    try {
      const response = await axios.get(`${API}/agent/chats/${id}`);
      setChatId(response.data.id);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error al cargar detalle de chat:', error);
    } finally {
      setLoading(false);
    }
  };

  // Iniciar un nuevo chat
  const handleNewChat = () => {
    setChatId(null);
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: `¡Hola **${user.full_name}**! 👋 Iniciamos una nueva consulta. ¿En qué te puedo asistir hoy?`,
        timestamp: new Date().toISOString()
      }
    ]);
    setActiveTab('chat');
  };

  // Enviar mensaje
  const handleSendMessage = async (e) => {
    e.preventDefault();
    const cleanMsg = inputMessage.trim();
    if (!cleanMsg || loading) return;

    setInputMessage('');
    setLoading(true);

    // Agregar mensaje del usuario a la vista local
    const tempUserMsg = {
      id: Math.random().toString(),
      role: 'user',
      content: cleanMsg,
      timestamp: new Date().toISOString()
    };
    
    // Si era el mensaje de bienvenida inicial, limpiar la lista antes de añadir
    const currentMsgs = messages.filter(m => m.id !== 'welcome');
    setMessages([...currentMsgs, tempUserMsg]);

    try {
      const response = await axios.post(`${API}/agent/chat`, {
        chat_id: chatId,
        message: cleanMsg
      });

      const { chat_id, response: aiRes } = response.data;
      setChatId(chat_id);
      
      // Agregar mensaje del asistente
      setMessages(prev => [
        ...prev,
        {
          id: Math.random().toString(), // Generado localmente temporal
          role: 'assistant',
          content: aiRes,
          timestamp: new Date().toISOString()
        }
      ]);
    } catch (error) {
      console.error('Error al conversar con el agente:', error);
      setMessages(prev => [
        ...prev,
        {
          id: 'error-' + Math.random(),
          role: 'assistant',
          content: '⚠️ Ocurrió un error de conexión con el agente. Asegúrate de configurar la clave de API de Gemini en el backend o reintenta en un momento.',
          timestamp: new Date().toISOString()
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Enviar retroalimentación (feedback)
  const handleFeedback = async (msgId, rating) => {
    if (!chatId || msgId.startsWith('welcome') || msgId.startsWith('error')) return;

    try {
      await axios.post(`${API}/agent/chats/${chatId}/messages/${msgId}/feedback`, {
        rating,
        comment: ''
      });
      setFeedbackSent(prev => ({
        ...prev,
        [msgId]: { rating }
      }));
    } catch (error) {
      console.error('Error al enviar feedback:', error);
    }
  };

  // Renderizador básico y seguro de Markdown para instrucciones
  const renderMessageContent = (text) => {
    if (!text) return '';

    // Reemplazar saltos de línea por <br/>
    let formatted = text;

    // Escapar HTML básico
    formatted = formatted
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Negritas (**texto**)
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Cursivas (*texto*)
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // Listas con viñetas
    formatted = formatted.replace(/^\s*[\-\*]\s+(.*)$/gm, '<li class="ml-4 list-disc">$1</li>');

    // Bloques de código sencillos (`código`)
    formatted = formatted.replace(/`(.*?)`/g, '<code class="bg-zinc-200 dark:bg-zinc-800 px-1 py-0.5 rounded text-xs font-mono font-bold">$1</code>');

    // Envolver listas consecutivas en <ul> si es necesario, pero para evitar bugs de regex
    // el renderizado directo inyectado con dangerouslySetInnerHTML funciona bien para estos tags simples.
    return (
      <div 
        className="text-sm leading-relaxed space-y-1 font-sans break-words"
        dangerouslySetInnerHTML={{ __html: formatted }} 
      />
    );
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      
      {/* PANEL DE CHAT (GLASSMORPHISM) */}
      {isOpen && (
        <div className="mb-4 w-96 max-w-[calc(100vw-2rem)] h-[550px] max-h-[80vh] flex flex-col bg-white/90 dark:bg-zinc-950/90 backdrop-blur-md border border-zinc-200/50 dark:border-zinc-800/50 shadow-2xl rounded-2xl overflow-hidden transition-all duration-300 transform scale-100 origin-bottom-right">
          
          {/* Cabecera */}
          <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 p-4 text-white flex items-center justify-between shadow-md">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-xl">
                <Brain className="w-5 h-5 animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold text-sm leading-tight flex items-center gap-1">
                  GKServicePOS-AI
                  <Sparkles className="w-3.5 h-3.5 text-yellow-300 animate-bounce" />
                </h3>
                <span className="text-[10px] text-indigo-100 font-medium">
                  {loading ? 'Pensando...' : 'En línea • Aprendiendo'}
                </span>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Subcabecera / Navegación */}
          <div className="flex border-b border-zinc-200 dark:border-zinc-800 text-xs bg-zinc-50 dark:bg-zinc-900/50">
            <button
              onClick={() => handleTabChange('chat')}
              className={`flex-1 py-2.5 text-center font-medium transition-colors ${activeTab === 'chat' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white dark:bg-zinc-950' : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'}`}
            >
              Asistente
            </button>
            <button
              onClick={() => handleTabChange('history')}
              className={`flex-1 py-2.5 text-center font-medium transition-colors ${activeTab === 'history' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white dark:bg-zinc-950' : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'}`}
            >
              Historial
            </button>
            {user.role === 'admin' && (
              <button
                onClick={() => handleTabChange('learnings')}
                className={`flex-1 py-2.5 text-center font-medium transition-colors ${activeTab === 'learnings' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-white dark:bg-zinc-950' : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'}`}
              >
                Memoria
              </button>
            )}
          </div>

          {/* Cuerpo del chat */}
          <div className="flex-1 overflow-y-auto p-4 bg-zinc-50/50 dark:bg-zinc-900/10 space-y-4">
            
            {/* VISTA 1: CHAT ACTIVO */}
            {activeTab === 'chat' && (
              <>
                {messages.map((msg) => {
                  const isUser = msg.role === 'user';
                  const feedback = feedbackSent[msg.id] || msg.feedback;
                  
                  return (
                    <div 
                      key={msg.id}
                      className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                    >
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm border ${
                        isUser 
                          ? 'bg-indigo-600 text-white border-indigo-500 rounded-tr-none' 
                          : 'bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 border-zinc-200 dark:border-zinc-800 rounded-tl-none'
                      }`}>
                        {renderMessageContent(msg.content)}
                      </div>
                      
                      {/* Retroalimentación en mensajes de IA (Excepto bienvenida/errores) */}
                      {!isUser && msg.id !== 'welcome' && !msg.id.startsWith('error') && (
                        <div className="flex items-center gap-2 mt-1 px-1.5">
                          <button
                            onClick={() => handleFeedback(msg.id, 1)}
                            className={`p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${
                              feedback?.rating === 1 ? 'text-green-600' : 'text-zinc-400'
                            }`}
                            title="Respuesta de ayuda útil"
                          >
                            <ThumbsUp className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleFeedback(msg.id, -1)}
                            className={`p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors ${
                              feedback?.rating === -1 ? 'text-red-500' : 'text-zinc-400'
                            }`}
                            title="Respuesta incorrecta o incompleta"
                          >
                            <ThumbsDown className="w-3.5 h-3.5" />
                          </button>
                          {feedback && (
                            <span className="text-[10px] text-zinc-400 font-medium">¡Gracias!</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Indicador de carga */}
                {loading && (
                  <div className="flex items-start gap-2">
                    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 px-4 py-3 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            )}

            {/* VISTA 2: HISTORIAL DE CHATS */}
            {activeTab === 'history' && (
              <div className="space-y-2">
                <button
                  onClick={handleNewChat}
                  className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-dashed border-indigo-300 hover:border-indigo-500 dark:border-zinc-700 dark:hover:border-indigo-600 rounded-xl text-xs font-semibold text-indigo-600 dark:text-indigo-400 bg-indigo-50/20 hover:bg-indigo-50/50 dark:bg-zinc-900/20 dark:hover:bg-indigo-950/20 transition-all mb-4"
                >
                  <MessageSquare className="w-4 h-4" />
                  Nueva Consulta
                </button>

                {pastChats.length === 0 ? (
                  <div className="text-center py-10 text-zinc-400">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                    <p className="text-xs">No tienes consultas archivadas en esta empresa.</p>
                  </div>
                ) : (
                  pastChats.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => handleSelectChat(c.id)}
                      className={`w-full text-left p-3 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 hover:border-indigo-300 dark:hover:border-zinc-700 transition-all flex items-center justify-between group ${
                        chatId === c.id ? 'border-indigo-500 ring-1 ring-indigo-500' : ''
                      }`}
                    >
                      <div className="truncate pr-4 flex-1">
                        <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                          {c.title}
                        </p>
                        <span className="text-[10px] text-zinc-400">
                          {new Date(c.updated_at).toLocaleDateString('es-CL', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <ArrowLeft className="w-3.5 h-3.5 text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity transform rotate-180" />
                    </button>
                  ))
                )}
              </div>
            )}

            {/* VISTA 3: MEMORIA (APRENDIZAJES) */}
            {activeTab === 'learnings' && user.role === 'admin' && (
              <div className="space-y-3">
                <div className="bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900/50 rounded-xl p-3 flex gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                  <div className="text-[11px] text-indigo-800 dark:text-indigo-300 leading-normal">
                    <strong>¿Qué es la memoria?</strong> Estos son hechos específicos de tu empresa que he extraído de nuestras conversaciones para recordarlos en futuras respuestas.
                  </div>
                </div>

                {learnings.length === 0 ? (
                  <div className="text-center py-10 text-zinc-400">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                    <p className="text-xs">Aún no he aprendido reglas de tu negocio. ¡Comencemos a hablar!</p>
                  </div>
                ) : (
                  learnings.map((l) => (
                    <div 
                      key={l.id}
                      className="p-3 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl text-xs space-y-1.5 shadow-sm"
                    >
                      <div className="flex items-center justify-between">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide uppercase ${
                          l.category === 'business_hours' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300' :
                          l.category === 'inventory_preference' ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300' :
                          l.category === 'credit_policy' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300' :
                          'bg-zinc-100 text-zinc-800 dark:bg-zinc-800 dark:text-zinc-300'
                        }`}>
                          {l.category === 'business_hours' ? 'Horario' :
                           l.category === 'inventory_preference' ? 'Inventario' :
                           l.category === 'credit_policy' ? 'Créditos' : 'General'}
                        </span>
                        
                        {l.is_approved ? (
                          <span className="flex items-center gap-1 text-[9px] font-medium text-emerald-600 dark:text-emerald-400">
                            <Check className="w-3 h-3" /> Activo
                          </span>
                        ) : (
                          <span className="text-[9px] font-medium text-zinc-400">Pausado</span>
                        )}
                      </div>
                      <p className="text-zinc-700 dark:text-zinc-300 leading-normal italic font-medium">
                        "{l.fact}"
                      </p>
                    </div>
                  ))
                )}
              </div>
            )}

          </div>

          {/* Footer - Entrada de chat */}
          {activeTab === 'chat' && (
            <div className="p-3 border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 flex flex-col gap-2">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Hazme una consulta sobre los flujos..."
                  disabled={loading}
                  className="flex-1 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 text-zinc-800 dark:text-zinc-100"
                />
                <button
                  type="submit"
                  disabled={loading || !inputMessage.trim()}
                  className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-300 text-white p-2 rounded-xl transition-all shadow disabled:shadow-none flex items-center justify-center shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
              <div className="text-[9px] text-zinc-400 text-center leading-tight">
                AI aprende de tu negocio y adapta sus respuestas. Respuestas con retroalimentación mejoran la precisión.
              </div>
            </div>
          )}

        </div>
      )}

      {/* BOTÓN FLOTANTE (DEGRADADO ANIMADO) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-gradient-to-tr from-indigo-600 via-indigo-700 to-purple-700 hover:from-indigo-500 hover:to-purple-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-105 active:scale-95 group focus:outline-none flex items-center justify-center relative overflow-hidden"
        title="Soporte y ayuda inteligente"
      >
        <span className="absolute inset-0 w-full h-full bg-gradient-to-tr from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000 ease-out" />
        
        {isOpen ? (
          <X className="w-6 h-6 animate-spin-once" />
        ) : (
          <>
            <Brain className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 bg-red-500 w-3 h-3 rounded-full border-2 border-white dark:border-zinc-950 animate-pulse"></span>
          </>
        )}
      </button>

    </div>
  );
}
